import { useState } from 'react'
import type { ReactElement } from 'react'
import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router'
import { LocaleSwitcher } from './components/locale-switcher'
import { ImpersonationBanner } from './components/impersonation-banner'
import { ProtectedRoute } from './components/protected-route'
import { AppI18nProvider, useTranslate } from './lib/i18n-provider'
import { SessionProvider, useSession } from './lib/session-context'
import { BookingsPage } from './routes/bookings-page'
import { ChannelsPage } from './routes/channels-page'
import { DashboardPage } from './routes/dashboard-page'
import { LoginPage } from './routes/login-page'
import { MediaPage } from './routes/media-page'
import { MfaPage } from './routes/mfa-page'
import { SettingsPage } from './routes/settings-page'
import { CalendarPage } from './routes/calendar-page'
import { CompliancePage } from './routes/compliance-page'
import { MessagesPage } from './routes/messages-page'
import { ContentPage } from './routes/content-page'
import { AccountPage } from './routes/account-page'
import { HandoverPage } from './routes/handover-page'
import { ForgotPasswordPage, ResetPasswordPage } from './routes/reset-page'
import { LocationPage } from './routes/location-page'
import { PricingPage } from './routes/pricing-page'
import { TasksPage } from './routes/tasks-page'
import { SystemPage } from './routes/system-page'
import { ThemePage } from './routes/theme-page'

/**
 * Applicatieschil: sessie, navigatie, taalkeuze en de routes van het dashboard.
 * Het dashboard heeft geen SEO-eis, dus client-side routing volstaat (§17.5).
 *
 * De opbouw van buiten naar binnen is niet vrijblijvend:
 *
 *   AppI18nProvider → SessionProvider → BrowserRouter → AppShell
 *
 * De sessieprovider staat *om* de router heen omdat `GET /auth/session` één keer
 * per paginabezoek gesteld hoort te worden, niet opnieuw bij iedere navigatie.
 * Zou hij binnen een route staan, dan zou hij bij elke routewissel opnieuw
 * gemonteerd worden en zou de sessie telkens even op `loading` staan — met een
 * knippering naar het inlogscherm als gevolg. Hij staat wél binnen de i18n-schil,
 * want de schermen die hij aanstuurt hebben vertaalde tekst nodig.
 *
 * Er staat geen letterlijke interfacetekst in dit bestand (§17.8). De naam van de
 * ingelogde gebruiker is de enige onvertaalde tekst op het scherm, en dat is
 * terecht: dat is data uit de database, geen interfacetekst.
 */

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'nav__link nav__link--active' : 'nav__link'
}

/**
 * Wie er ingelogd is, plus de uitlogknop. Staat alleen op het scherm zodra er
 * werkelijk een gebruiker is; tijdens `loading` en op het inlogscherm zou een
 * uitlogknop niets te doen hebben.
 */
function SessionControls(): ReactElement | null {
  const t = useTranslate()
  const { user, logout } = useSession()
  const [signingOut, setSigningOut] = useState(false)

  async function handleSignOut(): Promise<void> {
    setSigningOut(true)
    await logout()
    setSigningOut(false)
  }

  if (user === null) {
    return null
  }

  return (
    <>
      {/* De naam is data; alleen de omhullende zin komt uit de catalogus. */}
      <span className="app__user">
        {t('dashboard.auth.signedInAs', { name: user.displayName })}
      </span>
      <button
        className="button"
        disabled={signingOut}
        onClick={() => {
          // Bewust geen navigatie hier. `logout` zet de sessie op `anonymous` en
          // ProtectedRoute stuurt het huidige beschermde pad zelf naar /login.
          // Twee plekken die hetzelfde doorsturen lopen vroeg of laat uiteen.
          void handleSignOut()
        }}
        type="button"
      >
        {t('dashboard.auth.signOut')}
      </button>
    </>
  )
}

function AppShell(): ReactElement {
  const t = useTranslate()
  const { user } = useSession()

  return (
    <>
      {/* Buiten `.app`, want de balk hoort over de volle breedte te lopen: hij gaat
          niet over de inhoud maar over de omstandigheden waaronder je ernaar kijkt. */}
      <ImpersonationBanner />
      <div className="app">
        <header className="app__header">
          <span className="app__brand">{t('common.app.name')}</span>
          {/* De navigatie wijst uitsluitend naar beschermde schermen. Hem tonen aan
            wie niet ingelogd is levert links op die alleen naar /login leiden. */}
          {user !== null && (
            <nav className="nav" aria-label={t('dashboard.nav.label')}>
              <NavLink className={navLinkClass} to="/" end>
                {t('dashboard.nav.dashboard')}
              </NavLink>
              {/* De agenda hoort naast het overzicht: "welke dagen zijn vrij" is de
                vraag die het vaakst wordt gesteld, en de enige plek waar de
                verhuurder zelf een periode kan vrijhouden. */}
              <NavLink className={navLinkClass} to="/calendar">
                {t('dashboard.nav.calendar')}
              </NavLink>
              {/* Direct na het overzicht: de werkvoorraad met openstaande aanvragen
                is het scherm waar dagelijks iets te doen is (§17.13). */}
              <NavLink className={navLinkClass} to="/bookings">
                {t('dashboard.nav.bookings')}
              </NavLink>
              {/* Direct na de reserveringen: een conflict met een extern kanaal is
                een dubbele verhuur, en dat is de tweede plek waar dagelijks iets
                te doen kan zijn. */}
              <NavLink className={navLinkClass} to="/channels">
                {t('dashboard.nav.channels')}
              </NavLink>
              <NavLink className={navLinkClass} to="/tasks">
                {t('dashboard.nav.tasks')}
              </NavLink>
              {/* Naast de taken: wat er automatisch uitgaat is ook werk, alleen werk
                dat het platform doet. Een mislukt bericht is er dan weer wél een
                voor de verhuurder (§17.31). */}
              <NavLink className={navLinkClass} to="/messages">
                {t('dashboard.nav.messages')}
              </NavLink>
              {/* Vóór de foto's: de tekst is wat een gast als eerste leest, en zonder
                kop en omschrijving laat de publicatiecontrole de woning niet live. */}
              <NavLink className={navLinkClass} to="/content">
                {t('dashboard.nav.content')}
              </NavLink>
              {/* Naast de beschrijving: het adres en de kaartpositie horen bij hoe
                  de woning zich presenteert, en bepalen of hij in de gids te
                  vinden is (§17.39). */}
              <NavLink className={navLinkClass} to="/location">
                {t('dashboard.nav.location')}
              </NavLink>
              <NavLink className={navLinkClass} to="/media">
                {t('dashboard.nav.media')}
              </NavLink>
              <NavLink className={navLinkClass} to="/theme">
                {t('dashboard.nav.theme')}
              </NavLink>
              {/* Naast de agenda: wat een nacht kost hangt samen met hoe vol hij
                staat, en dat is precies wat de bezettingsregel doet (§17.32). */}
              <NavLink className={navLinkClass} to="/pricing">
                {t('dashboard.nav.pricing')}
              </NavLink>
              {/* Vlak vóór de instellingen: regelgeving is een instelling met
                rechtsgevolgen en verdient een eigen ingang (§17.15). */}
              <NavLink className={navLinkClass} to="/compliance">
                {t('dashboard.nav.compliance')}
              </NavLink>
              <NavLink className={navLinkClass} to="/settings">
                {t('dashboard.nav.settings')}
              </NavLink>
              {/* De eigen rekening, náást de instellingen van de woning: dat zijn
                  twee verschillende dingen, en het wachtwoord hoort niet weggestopt
                  onder de prijsafspraken (§17.45). */}
              <NavLink className={navLinkClass} to="/account">
                {t('dashboard.nav.account')}
              </NavLink>
              <NavLink className={navLinkClass} to="/system">
                {t('dashboard.nav.system')}
              </NavLink>
            </nav>
          )}
          <SessionControls />
          <LocaleSwitcher />
        </header>

        <main className="app__main">
          <Routes>
            {/* Publiek: hier komt iemand terecht die nog geen sessie heeft. */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/mfa" element={<MfaPage />} />
            {/* Publiek en met opzet buiten ProtectedRoute: wie hier aankomt heeft nog
              geen sessie op deze hostnaam — dat is precies wat een overdracht
              oplost (§17.43). */}
            <Route path="/handover" element={<HandoverPage />} />
            {/* Publiek, en dat moet: wie zijn wachtwoord vergeten is, kan per definitie
              niet inloggen (§17.47). */}
            <Route path="/forgot" element={<ForgotPasswordPage />} />
            <Route path="/reset" element={<ResetPasswordPage />} />

            {/* Alles daaronder vereist een sessie. ProtectedRoute is een
              layout-route: de schermen komen er via <Outlet /> uit. Dit is geen
              beveiliging — die staat in de API en in de RLS-policies (§17.3) —
              maar voorkomt schermen die toch geen data zouden tonen. */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/bookings" element={<BookingsPage />} />
              <Route path="/channels" element={<ChannelsPage />} />
              <Route path="/content" element={<ContentPage />} />
              <Route path="/location" element={<LocationPage />} />
              <Route path="/media" element={<MediaPage />} />
              <Route path="/theme" element={<ThemePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/compliance" element={<CompliancePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/system" element={<SystemPage />} />
            </Route>

            {/* Onbekende paden gaan terug naar het overzicht; een 404-scherm heeft
              in een afgeschermd dashboard geen toegevoegde waarde. Wie niet
              ingelogd is, komt van daaruit alsnog bij /login uit. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </>
  )
}

export function App(): ReactElement {
  return (
    <AppI18nProvider>
      <SessionProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </SessionProvider>
    </AppI18nProvider>
  )
}
